import type {
  OnboardingAsset,
  OnboardingCountryCode,
  OnboardingCurrencyCode,
  OnboardingLiability,
} from '../types/onboarding';

export const DEFAULT_EXPECTED_RETURN_PCT = 8;
export const MIN_EXPECTED_RETURN_PCT = 4;
export const MAX_EXPECTED_RETURN_PCT = 12;

export type IncomeSliderMarks = {
  min: number;
  mid: number;
  high: number;
  max: number;
  step: number;
  currencySymbol: string;
  currencyCode: OnboardingCurrencyCode;
};

export type CatalogAssetTemplate = {
  type: string;
  label: string;
  isInvestment: boolean;
  defaultReturnPct?: number;
};

export type CatalogLiabilityTemplate = {
  type: string;
  label: string;
  /** When true, UI asks for rate + payment and shows interest vs principal. */
  detailed: boolean;
};

const US_ASSETS: CatalogAssetTemplate[] = [
  { type: '401k', label: '401(k)', isInvestment: true, defaultReturnPct: DEFAULT_EXPECTED_RETURN_PCT },
  { type: 'roth_ira', label: 'Roth IRA', isInvestment: true, defaultReturnPct: DEFAULT_EXPECTED_RETURN_PCT },
  { type: 'traditional_ira', label: 'Traditional IRA', isInvestment: true, defaultReturnPct: DEFAULT_EXPECTED_RETURN_PCT },
  { type: 'brokerage', label: 'Brokerage', isInvestment: true, defaultReturnPct: DEFAULT_EXPECTED_RETURN_PCT },
  { type: 'home', label: 'Primary home', isInvestment: false },
  { type: 'car', label: 'Car', isInvestment: false },
  { type: 'cash', label: 'Cash / emergency fund', isInvestment: false },
];

const CA_ASSETS: CatalogAssetTemplate[] = [
  { type: 'tfsa', label: 'TFSA', isInvestment: true, defaultReturnPct: DEFAULT_EXPECTED_RETURN_PCT },
  { type: 'rrsp', label: 'RRSP', isInvestment: true, defaultReturnPct: DEFAULT_EXPECTED_RETURN_PCT },
  { type: 'fhsa', label: 'FHSA', isInvestment: true, defaultReturnPct: DEFAULT_EXPECTED_RETURN_PCT },
  { type: 'brokerage', label: 'Non-registered', isInvestment: true, defaultReturnPct: DEFAULT_EXPECTED_RETURN_PCT },
  { type: 'home', label: 'Primary home', isInvestment: false },
  { type: 'car', label: 'Car', isInvestment: false },
  { type: 'cash', label: 'Cash / emergency fund', isInvestment: false },
];

const GENERIC_ASSETS: CatalogAssetTemplate[] = [
  { type: 'retirement', label: 'Retirement account', isInvestment: true, defaultReturnPct: DEFAULT_EXPECTED_RETURN_PCT },
  { type: 'brokerage', label: 'Investments / brokerage', isInvestment: true, defaultReturnPct: DEFAULT_EXPECTED_RETURN_PCT },
  { type: 'home', label: 'Primary home', isInvestment: false },
  { type: 'car', label: 'Car', isInvestment: false },
  { type: 'cash', label: 'Cash / savings', isInvestment: false },
];

const DEFAULT_LIABILITIES: CatalogLiabilityTemplate[] = [
  { type: 'mortgage', label: 'Home loan / mortgage', detailed: true },
  { type: 'auto', label: 'Auto loan', detailed: false },
  { type: 'credit', label: 'Credit cards', detailed: false },
  { type: 'other', label: 'Other debt', detailed: false },
];

const INCOME_MARKS: Record<OnboardingCountryCode, IncomeSliderMarks> = {
  US: {
    min: 2000,
    mid: 5500,
    high: 12000,
    max: 25000,
    step: 250,
    currencySymbol: '$',
    currencyCode: 'USD',
  },
  CA: {
    min: 2000,
    mid: 5000,
    high: 10000,
    max: 20000,
    step: 250,
    currencySymbol: '$',
    currencyCode: 'CAD',
  },
  IN: {
    min: 20000,
    mid: 80000,
    high: 200000,
    max: 500000,
    step: 5000,
    currencySymbol: '₹',
    currencyCode: 'INR',
  },
  GB: {
    min: 1500,
    mid: 3500,
    high: 8000,
    max: 15000,
    step: 100,
    currencySymbol: '£',
    currencyCode: 'GBP',
  },
  OTHER: {
    min: 1000,
    mid: 4000,
    high: 10000,
    max: 20000,
    step: 250,
    currencySymbol: '$',
    currencyCode: 'USD',
  },
};

export const COUNTRY_OPTIONS: { code: OnboardingCountryCode; label: string }[] = [
  { code: 'US', label: 'United States' },
  { code: 'CA', label: 'Canada' },
  { code: 'IN', label: 'India' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'OTHER', label: 'Other' },
];

export const SPEND_CATEGORY_OPTIONS: { id: string; name: string }[] = [
  { id: 'household', name: 'Household' },
  { id: 'groceries', name: 'Groceries' },
  { id: 'dining', name: 'Dining' },
  { id: 'transport', name: 'Transport' },
  { id: 'subscriptions', name: 'Subscriptions' },
  { id: 'entertainment', name: 'Entertainment' },
  { id: 'health', name: 'Health' },
  { id: 'shopping', name: 'Shopping' },
];

export function getIncomeMarks(countryCode: OnboardingCountryCode): IncomeSliderMarks {
  return INCOME_MARKS[countryCode];
}

export function getCurrencyForCountry(countryCode: OnboardingCountryCode): OnboardingCurrencyCode {
  return INCOME_MARKS[countryCode].currencyCode;
}

export function getAssetCatalog(countryCode: OnboardingCountryCode): CatalogAssetTemplate[] {
  if (countryCode === 'US') return US_ASSETS;
  if (countryCode === 'CA') return CA_ASSETS;
  return GENERIC_ASSETS;
}

export function getLiabilityCatalog(): CatalogLiabilityTemplate[] {
  return DEFAULT_LIABILITIES;
}

export function seedAssetsFromCatalog(countryCode: OnboardingCountryCode): OnboardingAsset[] {
  return getAssetCatalog(countryCode).map((item) => ({
    type: item.type,
    label: item.label,
    value: 0,
    isInvestment: item.isInvestment,
    expectedReturnPct: item.isInvestment
      ? (item.defaultReturnPct ?? DEFAULT_EXPECTED_RETURN_PCT)
      : undefined,
  }));
}

export function seedLiabilitiesFromCatalog(): OnboardingLiability[] {
  return getLiabilityCatalog().map((item) => ({
    type: item.type,
    label: item.label,
    balance: 0,
    interestRatePct: item.detailed ? 6.5 : undefined,
    monthlyPayment: item.detailed ? 0 : undefined,
  }));
}

export function formatMoney(amount: number, countryCode: OnboardingCountryCode): string {
  const symbol = getIncomeMarks(countryCode).currencySymbol;
  return `${symbol}${Math.round(amount).toLocaleString()}`;
}

export function currencyLabel(countryCode: OnboardingCountryCode): string {
  const marks = getIncomeMarks(countryCode);
  return `${marks.currencyCode} (${marks.currencySymbol})`;
}
